/**
 * A WinAmp-style, WebGL-powered visualizer for all the live DSP signals.
 * Draws a chunky multi-band spectrum-ish bar array (driven by the feature
 * vector), a scrolling oscilloscope-style history trail, a pulsing radial
 * "plasma" core reacting to energy/pitch, and floating particles that spawn
 * on transients. Falls back gracefully if WebGL is unavailable.
 *
 * This is intentionally "wacky fun time": lots of bloom, hue cycling, and
 * beat-reactive motion. It reads only from the feature vector so it works
 * with the existing engine event stream without any new plumbing.
 */
const FEATURE_BANDS = [
    "energy",
    "centroid",
    "voicing",
    "noise",
    "transient",
    "flatness",
    "rolloff",
    "tilt",
    "spread",
    "crest",
    "flux",
    "zcr",
    "hnr",
    "pitchConf",
];
const VERT_SRC = `
        attribute vec2 a_pos;
        attribute vec4 a_color;
        varying vec4 v_color;
        uniform vec2 u_res;
        void main() {
            vec2 clip = (a_pos / u_res) * 2.0 - 1.0;
            gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
            v_color = a_color;
        }
    `;
const FRAG_SRC = `
        precision mediump float;
        varying vec4 v_color;
        void main() {
            gl_FragColor = v_color;
        }
    `;
export class WebGLVisualizer {
    constructor(canvas) {
        Object.defineProperty(this, "canvas", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: canvas
        });
        Object.defineProperty(this, "gl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "ctx2d", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "program", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "posBuf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "colBuf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "aPos", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "aColor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "uRes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "dpr", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "raf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "running", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "phase", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "smooth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "history", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "particles", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "prevTransient", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "prevEnergy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "beatFlash", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "resizeObserver", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // Dynamic geometry buffers (grown as needed).
        Object.defineProperty(this, "posData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Float32Array(4096)
        });
        Object.defineProperty(this, "colData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Float32Array(8192)
        });
        Object.defineProperty(this, "vertCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "onResize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => this.resize()
        });
        this.smooth = zeroFeatures();
        const gl = canvas.getContext("webgl", {
            alpha: false,
            antialias: true,
            premultipliedAlpha: false,
        }) ??
            canvas.getContext("experimental-webgl");
        if (gl) {
            this.gl = gl;
            this.initGl();
            console.info("[WebGLVisualizer] WebGL context acquired");
        }
        else {
            console.warn("[WebGLVisualizer] WebGL unavailable; using 2D fallback");
            this.ctx2d = canvas.getContext("2d");
        }
        this.resize();
        window.addEventListener("resize", this.onResize);
    }
    start() {
        if (this.running)
            return;
        this.running = true;
        // Ensure canvas is sized against real layout before first frame.
        requestAnimationFrame(() => this.resize());
        // A second deferred resize covers the case where layout is still
        // settling (the visualizer canvas fills a flex/absolute container
        // that may report 0×0 on the very first frame).
        setTimeout(() => this.resize(), 60);
        // A few more deferred resizes catch slow layout/route transitions so
        // the canvas never gets stuck at 0×0 (which renders nothing).
        setTimeout(() => this.resize(), 200);
        setTimeout(() => this.resize(), 600);
        // Keep the canvas correctly sized even if the container is resized or
        // laid out late (e.g. entering fullscreen or rotating a device).
        if (typeof ResizeObserver !== "undefined") {
            try {
                this.resizeObserver = new ResizeObserver(() => this.resize());
                this.resizeObserver.observe(this.canvas);
            }
            catch {
                this.resizeObserver = null;
            }
        }
        const loop = () => {
            if (!this.running)
                return;
            this.frame();
            this.raf = requestAnimationFrame(loop);
        };
        this.raf = requestAnimationFrame(loop);
    }
    stop() {
        this.running = false;
        cancelAnimationFrame(this.raf);
        this.raf = 0;
    }
    dispose() {
        this.stop();
        window.removeEventListener("resize", this.onResize);
        if (this.resizeObserver) {
            try {
                this.resizeObserver.disconnect();
            }
            catch { }
            this.resizeObserver = null;
        }
        if (this.gl) {
            if (this.program)
                this.gl.deleteProgram(this.program);
            if (this.posBuf)
                this.gl.deleteBuffer(this.posBuf);
            if (this.colBuf)
                this.gl.deleteBuffer(this.colBuf);
        }
    }
    /** Push a fresh feature vector to react to. */
    feed(f) {
        if (!f)
            return;
        for (const k of FEATURE_BANDS) {
            const v = f[k] ?? 0;
            this.smooth[k] += (v - (this.smooth[k] ?? 0)) * 0.28;
        }
        this.smooth.pitchHz = f.pitchHz ?? 0;
        const transient = f.transient ?? 0;
        const energy = f.energy ?? 0;
        // Detect an onset "beat" from a rising transient / energy jump.
        if ((transient > 0.25 && transient > this.prevTransient + 0.05) ||
            energy - this.prevEnergy > 0.2) {
            this.beatFlash = 1;
            this.spawnBurst(energy, f.centroid ?? 0.5);
        }
        this.prevTransient = transient;
        this.prevEnergy = energy;
    }
    initGl() {
        const gl = this.gl;
        if (!gl)
            return;
        const vs = this.compile(gl.VERTEX_SHADER, VERT_SRC);
        const fs = this.compile(gl.FRAGMENT_SHADER, FRAG_SRC);
        if (!vs || !fs)
            return;
        const prog = gl.createProgram();
        if (!prog)
            return;
        gl.attachShader(prog, vs);
        gl.attachShader(prog, fs);
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            console.error("[WebGLVisualizer] link failed", gl.getProgramInfoLog(prog));
            return;
        }
        this.program = prog;
        this.aPos = gl.getAttribLocation(prog, "a_pos");
        this.aColor = gl.getAttribLocation(prog, "a_color");
        this.uRes = gl.getUniformLocation(prog, "u_res");
        this.posBuf = gl.createBuffer();
        this.colBuf = gl.createBuffer();
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    }
    compile(type, src) {
        const gl = this.gl;
        if (!gl)
            return null;
        const sh = gl.createShader(type);
        if (!sh)
            return null;
        gl.shaderSource(sh, src);
        gl.compileShader(sh);
        if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
            console.error("[WebGLVisualizer] shader compile failed", gl.getShaderInfoLog(sh));
            gl.deleteShader(sh);
            return null;
        }
        return sh;
    }
    resize() {
        this.dpr = Math.min(2, window.devicePixelRatio || 1);
        const rect = this.canvas.getBoundingClientRect();
        const w = this.canvas.clientWidth || rect.width || window.innerWidth;
        const h = this.canvas.clientHeight || rect.height || window.innerHeight;
        const nw = Math.max(1, Math.floor(w * this.dpr));
        const nh = Math.max(1, Math.floor(h * this.dpr));
        if (this.canvas.width !== nw || this.canvas.height !== nh) {
            this.canvas.width = nw;
            this.canvas.height = nh;
        }
    }
    frame() {
        this.phase += 0.016;
        this.beatFlash *= 0.9;
        if (this.beatFlash < 0.01)
            this.beatFlash = 0;
        // Record history for the scrolling scope (energy + centroid).
        this.history.push([
            this.smooth.energy ?? 0,
            this.smooth.centroid ?? 0,
            this.smooth.voicing ?? 0,
        ]);
        if (this.history.length > 256) {
            this.history.splice(0, this.history.length - 256);
        }
        this.updateParticles();
        if (this.gl)
            this.frameGl();
        else
            this.frame2d();
    }
    frameGl() {
        const gl = this.gl;
        if (!gl || !this.program)
            return;
        const w = this.canvas.width;
        const h = this.canvas.height;
        if (w === 0 || h === 0) {
            this.resize();
            return;
        }
        gl.viewport(0, 0, w, h);
        // A dark, slightly beat-flashed background.
        const bf = this.beatFlash * 0.08;
        gl.clearColor(0.02 + bf, 0.03 + bf, 0.08 + bf, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.useProgram(this.program);
        gl.uniform2f(this.uRes, w, h);
        this.vertCount = 0;
        this.buildBars(w, h);
        this.buildScope(w, h);
        this.buildCore(w, h);
        this.buildParticles(w, h);
        this.flush(gl);
    }
    ensureCapacity(extraVerts) {
        const needPos = (this.vertCount + extraVerts) * 2;
        const needCol = (this.vertCount + extraVerts) * 4;
        if (needPos > this.posData.length) {
            const grown = new Float32Array(Math.max(needPos, this.posData.length * 2));
            grown.set(this.posData);
            this.posData = grown;
        }
        if (needCol > this.colData.length) {
            const grown = new Float32Array(Math.max(needCol, this.colData.length * 2));
            grown.set(this.colData);
            this.colData = grown;
        }
    }
    pushTri(x1, y1, x2, y2, x3, y3, r, g, b, a) {
        this.ensureCapacity(3);
        const p = this.vertCount * 2;
        const c = this.vertCount * 4;
        this.posData[p] = x1;
        this.posData[p + 1] = y1;
        this.posData[p + 2] = x2;
        this.posData[p + 3] = y2;
        this.posData[p + 4] = x3;
        this.posData[p + 5] = y3;
        for (let i = 0; i < 3; i++) {
            this.colData[c + i * 4] = r;
            this.colData[c + i * 4 + 1] = g;
            this.colData[c + i * 4 + 2] = b;
            this.colData[c + i * 4 + 3] = a;
        }
        this.vertCount += 3;
    }
    pushQuad(x, y, w, h, r, g, b, a) {
        this.pushTri(x, y, x + w, y, x, y + h, r, g, b, a);
        this.pushTri(x + w, y, x + w, y + h, x, y + h, r, g, b, a);
    }
    buildBars(w, h) {
        const n = FEATURE_BANDS.length;
        const gap = w * 0.004;
        const barW = (w - gap * (n + 1)) / n;
        const baseY = h * 0.82;
        for (let i = 0; i < n; i++) {
            const key = FEATURE_BANDS[i];
            let v = Math.max(0, Math.min(1, this.smooth[key] ?? 0));
            if (key === "pitchConf")
                v = this.smooth.pitchConf ?? 0;
            const barH = v * h * 0.55;
            const x = gap + i * (barW + gap);
            const hue = (i / n) * 360 + this.phase * 40;
            const [r, g, b] = hsl(hue % 360, 0.85, 0.55);
            // Chunky segmented WinAmp bars.
            const segH = h * 0.018;
            const segGap = h * 0.006;
            let yy = baseY;
            let drawn = 0;
            while (drawn < barH) {
                const t = drawn / (h * 0.55);
                const bright = 0.5 + t * 0.5 + this.beatFlash * 0.4;
                this.pushQuad(x, yy - segH, barW, segH, r * bright, g * bright, b * bright, 0.9);
                yy -= segH + segGap;
                drawn += segH + segGap;
            }
            // A "peak cap" that floats above.
            this.pushQuad(x, baseY - barH - segH * 1.2, barW, segH * 0.6, 1, 1, 1, 0.7);
        }
    }
    buildScope(w, h) {
        const len = this.history.length;
        if (len < 2)
            return;
        const midY = h * 0.28;
        const step = w / 255;
        const amp = h * 0.16;
        for (let i = 1; i < len; i++) {
            const a0 = this.history[i - 1];
            const a1 = this.history[i];
            const x0 = (256 - len + i - 1) * step;
            const x1 = (256 - len + i) * step;
            const y0 = midY - (a0[0] - 0.5) * 2 * amp;
            const y1 = midY - (a1[0] - 0.5) * 2 * amp;
            const hue = (a1[1] * 260 + this.phase * 60) % 360;
            const [r, g, b] = hsl(hue, 0.9, 0.6);
            const thick = 2 + a1[2] * 6;
            this.pushLine(x0, y0, x1, y1, thick, r, g, b, 0.85);
        }
    }
    pushLine(x0, y0, x1, y1, thick, r, g, b, a) {
        const dx = x1 - x0;
        const dy = y1 - y0;
        const len = Math.hypot(dx, dy) || 1;
        const nx = (-dy / len) * (thick / 2);
        const ny = (dx / len) * (thick / 2);
        this.pushTri(x0 + nx, y0 + ny, x0 - nx, y0 - ny, x1 + nx, y1 + ny, r, g, b, a);
        this.pushTri(x0 - nx, y0 - ny, x1 - nx, y1 - ny, x1 + nx, y1 + ny, r, g, b, a);
    }
    buildCore(w, h) {
        const cx = w * 0.5;
        const cy = h * 0.42;
        const energy = this.smooth.energy ?? 0;
        const pitch = this.smooth.pitchHz ?? 0;
        const pitchT = pitch > 0 ? Math.min(1, Math.max(0, (Math.log2(pitch) - 6) / 4)) : 0;
        const baseR = Math.min(w, h) * (0.08 + energy * 0.22) * (1 + this.beatFlash * 0.5);
        const segments = 48;
        const hueBase = (this.phase * 80 + pitchT * 200) % 360;
        for (let ring = 0; ring < 3; ring++) {
            const rr = baseR * (1 + ring * 0.4);
            const alpha = (0.25 - ring * 0.07) * (0.5 + energy);
            const [r, g, b] = hsl((hueBase + ring * 40) % 360, 0.9, 0.6);
            let px = cx + rr;
            let py = cy;
            for (let s = 1; s <= segments; s++) {
                const ang = (s / segments) * Math.PI * 2;
                const wob = 1 +
                    Math.sin(ang * 5 + this.phase * 3) * 0.12 * (this.smooth.flux ?? 0) +
                    Math.sin(ang * 3 - this.phase * 2) * 0.1 * (this.smooth.noise ?? 0);
                const nx = cx + Math.cos(ang) * rr * wob;
                const ny = cy + Math.sin(ang) * rr * wob;
                this.pushTri(cx, cy, px, py, nx, ny, r, g, b, alpha);
                px = nx;
                py = ny;
            }
        }
    }
    buildParticles(_w, _h) {
        for (const p of this.particles) {
            const [r, g, b] = hsl(p.hue % 360, 0.9, 0.6);
            const a = p.life * 0.8;
            const s = p.size * (0.5 + p.life);
            this.pushQuad(p.x - s / 2, p.y - s / 2, s, s, r, g, b, a);
        }
    }
    flush(gl) {
        if (this.vertCount === 0)
            return;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuf);
        gl.bufferData(gl.ARRAY_BUFFER, this.posData.subarray(0, this.vertCount * 2), gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(this.aPos);
        gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.colBuf);
        gl.bufferData(gl.ARRAY_BUFFER, this.colData.subarray(0, this.vertCount * 4), gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(this.aColor);
        gl.vertexAttribPointer(this.aColor, 4, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLES, 0, this.vertCount);
    }
    spawnBurst(energy, centroid) {
        const w = this.canvas.width;
        const h = this.canvas.height;
        const cx = w * 0.5;
        const cy = h * 0.42;
        const count = Math.round(8 + energy * 40);
        for (let i = 0; i < count; i++) {
            const ang = Math.random() * Math.PI * 2;
            const spd = (2 + Math.random() * 8) * (0.5 + energy) * this.dpr;
            this.particles.push({
                x: cx,
                y: cy,
                vx: Math.cos(ang) * spd,
                vy: Math.sin(ang) * spd,
                life: 1,
                hue: centroid * 300 + Math.random() * 60 + this.phase * 40,
                size: (4 + Math.random() * 10) * this.dpr,
            });
        }
        if (this.particles.length > 800) {
            this.particles.splice(0, this.particles.length - 800);
        }
    }
    updateParticles() {
        for (const p of this.particles) {
            p.x += p.vx;
            p.y += p.vy;
            p.vy += 0.15 * this.dpr;
            p.vx *= 0.98;
            p.life -= 0.02;
        }
        this.particles = this.particles.filter((p) => p.life > 0);
    }
    // ---- 2D fallback (no WebGL) ------------------------------------
    frame2d() {
        const ctx = this.ctx2d;
        if (!ctx)
            return;
        const w = this.canvas.width;
        const h = this.canvas.height;
        if (w === 0 || h === 0) {
            this.resize();
            return;
        }
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = `rgba(6,8,18,${0.35 + this.beatFlash * 0.2})`;
        ctx.fillRect(0, 0, w, h);
        ctx.globalCompositeOperation = "lighter";
        const n = FEATURE_BANDS.length;
        const barW = w / n;
        const baseY = h * 0.82;
        for (let i = 0; i < n; i++) {
            const v = Math.max(0, Math.min(1, this.smooth[FEATURE_BANDS[i]] ?? 0));
            const barH = v * h * 0.55;
            const hue = ((i / n) * 360 + this.phase * 40) % 360;
            ctx.fillStyle = `hsl(${hue},85%,55%)`;
            ctx.fillRect(i * barW + 2, baseY - barH, barW - 4, barH);
        }
        const cx = w * 0.5;
        const cy = h * 0.42;
        const energy = this.smooth.energy ?? 0;
        const rad = Math.min(w, h) * (0.1 + energy * 0.25) * (1 + this.beatFlash * 0.5);
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
        grad.addColorStop(0, `hsla(${(this.phase * 80) % 360},90%,60%,0.6)`);
        grad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, rad, 0, Math.PI * 2);
        ctx.fill();
        for (const p of this.particles) {
            ctx.globalAlpha = p.life;
            ctx.fillStyle = `hsl(${p.hue % 360},90%,60%)`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 0.5, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
    }
}
function zeroFeatures() {
    return {
        centroid: 0,
        noise: 0,
        voicing: 0,
        energy: 0,
        transient: 0,
        flatness: 0,
        rolloff: 0,
        tilt: 0,
        spread: 0,
        crest: 0,
        flux: 0,
        zcr: 0,
        hnr: 0,
        pitchHz: 0,
        pitchConf: 0,
        pitchClass: -1,
    };
}
function hsl(h, s, l) {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const hp = h / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let r = 0, g = 0, b = 0;
    if (hp < 1)
        [r, g, b] = [c, x, 0];
    else if (hp < 2)
        [r, g, b] = [x, c, 0];
    else if (hp < 3)
        [r, g, b] = [0, c, x];
    else if (hp < 4)
        [r, g, b] = [0, x, c];
    else if (hp < 5)
        [r, g, b] = [x, 0, c];
    else
        [r, g, b] = [c, 0, x];
    const m = l - c / 2;
    return [r + m, g + m, b + m];
}
